import './styles.css';
import { loadScript } from './script/loader';
import { ScenePlayer } from './ui/scene';
import { renderTitleScreen } from './ui/title-screen';
import { renderSaveLoadModal } from './ui/save-load-modal';
import { renderSettingsPanel } from './ui/settings';
import { renderCreditsPanel } from './ui/credits';
import { mountMasterMuteButton } from './ui/master-mute-button';
import { loadAutosave } from './engine/save-manager';
import type { Script } from './script/schema';

const app = document.querySelector<HTMLDivElement>('#app')!;

// Persistent mute button (always visible at top-right, across title + scenes).
mountMasterMuteButton();

let currentPlayer: ScenePlayer | null = null;

function showTitleScreen(script: Script): void {
  // Clean up any existing player
  if (currentPlayer) {
    currentPlayer.destroy();
    currentPlayer = null;
  }

  // Clean up previous title screen keyboard handler if any
  const prev = app.querySelector('#title-screen') as HTMLDivElement & { __cleanup?: () => void } | null;
  prev?.__cleanup?.();

  renderTitleScreen(app, script.meta.title, {
    onNewGame: () => {
      cleanupTitle();
      startGame(script);
    },
    onContinue: () => {
      const entry = loadAutosave();
      if (entry) {
        cleanupTitle();
        startGame(script, entry.state);
      } else {
        // Fallback to new game if autosave is somehow gone
        cleanupTitle();
        startGame(script);
      }
    },
    onLoad: () => {
      renderSaveLoadModal(app, 'load', {
        onLoad: (state) => {
          cleanupTitle();
          startGame(script, state);
        },
        onClose: () => {
          // Re-render title screen when modal is closed
          showTitleScreen(script);
        },
      });
    },
    onSettings: () => {
      renderSettingsPanel(app, {
        onClose: () => {
          // Settings modal removed, title screen still visible
        },
      });
    },
    onCredits: () => {
      renderCreditsPanel(app, {
        onClose: () => {
          // Credits modal removed, title screen still visible
        },
      });
    },
  });
}

function cleanupTitle(): void {
  const titleEl = app.querySelector('#title-screen') as HTMLDivElement & { __cleanup?: () => void } | null;
  titleEl?.__cleanup?.();
}

function startGame(
  script: Script,
  savedState?: import('./engine/game').GameState,
): void {
  currentPlayer = new ScenePlayer(
    script,
    app,
    (epilogue) => {
      console.log('Game ended:', epilogue);
    },
    () => {
      showTitleScreen(script);
    },
    savedState,
  );
}

async function boot(): Promise<void> {
  try {
    const script = await loadScript('./threadbare.vn.json');
    showTitleScreen(script);
  } catch (err: unknown) {
    // Fallback: show boot message if no script is available
    app.textContent = 'THREADBARE — boot test ok';
    console.warn('No script loaded, showing boot screen:', err);
  }
}

boot();
